import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const HowToUseADUHomeSchool = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-home-school" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-background to-muted py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            How to Use Your ADU as a Homeschool Classroom
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[hsl(var(--brand-primary))]">
            Give Your Kids the Learning Space They Deserve
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            A separate structure designed for focused learning can completely transform your homeschooling experience. From whiteboards and reading corners to maker spaces and quiet study zones—UDA designs ADUs that make learning joyful and distraction-free.
          </p>
          
          {/* Top CTA Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Homeschool Photos Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/c5e92a33-d12b-4ddb-b4ee-2631c7ed0a34.png" 
                  alt="Light-filled interior with built-in shelving"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/667ed660-4768-4028-8398-2f35700c5221.png" 
                  alt="Family-friendly setup with learning areas"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/9cb6fc36-840b-4f5b-a74b-8d9ad19dc0b7.png" 
                  alt="Organized learning space with storage solutions"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[hsl(var(--brand-primary))]">
            Learning-Focused Design Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Built-in Cabinetry and Cubbies</h3>
                <p className="text-muted-foreground">
                  Organized storage solutions for books, supplies, and educational materials that keep the learning space tidy and functional.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Writable Walls and Pegboards</h3>
                <p className="text-muted-foreground">
                  Interactive surfaces that encourage creativity, problem-solving, and hands-on learning experiences.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Soundproofed Study Nooks</h3>
                <p className="text-muted-foreground">
                  Quiet zones for focused reading, testing, and individual work without distractions from the main household.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Flex Space for Art, Music, and Science</h3>
                <p className="text-muted-foreground">
                  Adaptable areas that can accommodate different subjects, from art projects to science experiments to music practice.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Call-to-Action Section */}
      <section className="py-16 px-4 bg-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary-foreground))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Create the Perfect Learning Environment?</h2>
          <p className="text-lg mb-12 opacity-90">
            Whether you're homeschooling or supplementing education at home—your kids deserve a space built for success.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 w-full"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/our-work" 
                className="w-full"
              >
                🏡 Our Portfolio ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/faq" 
                className="w-full"
              >
                ❓ Our FAQ ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
      <FloatingADUWizard />
    </div>
  );
};

export default HowToUseADUHomeSchool;